<template>
  <v-card class="mx-auto" flat :height="cardHeight" max-width="1280" width="100%">
    <v-card-text class="pa-0">
      <v-row class="flex-nowrap">
        <v-col
          cols="3"
          id="previewChartsList"
          class="mx-1 flex-grow-0 flex-shrink-1"
        >
          <slider-charts
            :typeSelected="selectedChart.id || selectedChart.type"
            :chartTypes="chartTypes"
            :sliderHeight="cardHeight"
            :isQualitative="isQualitative"
            @selectedChart="selectChart"
          />
        </v-col>
        <v-col
          cols="9"
          id="selectedChartSpace"
          class="mx-1 flex-grow-0 flex-shrink-1"
        >
          <chart-preview
            :type="selectedChart.type"
            :data="selectedChart.data"
            :labels="selectedChart.labels"
            :target-value="sessionItem.definition.targetValue"
            :cardHeight="cardHeight"
            :isQualitative="isQualitative"
            :horizontal="selectedChart.horizontal"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import ChartPreview from "./ChartPreview.vue";
import SliderCharts from "@/components/SliderCharts.vue";
const props = defineProps({
  sessionItem: Object,
  containerHeight: String,
});
const emit = defineEmits(["update-chart-type"]);
const cardHeight = computed(() => {
  return "calc(" + props.containerHeight + " - 52px)";
});

// Determina se il KPI è qualitativo o quantitativo
const isQualitative = computed(() => {
  return props.sessionItem?.definition?.valueType === "QUALITATIVE";
});

const chartData = ref(
  isQualitative.value
    ? generateQualitativeData(
        props.sessionItem.definition.allowedValues,
        props.sessionItem.definition.targetValue,
      )
    : createArrayOfRandomValues(props.sessionItem.definition.targetValue),
);

const chartLabels = ref(
  isQualitative.value
    ? props.sessionItem.definition.allowedValues || []
    : [
        "12:00",
        "13:00",
        "14:00",
        "15:00",
        "16:00",
        "17:00",
        "18:00",
        "19:00",
        "20:00",
        "21:00",
        "22:00",
        "23:00",
      ],
);

// Grafici per KPI quantitativi
const quantitativeCharts = [
  {
    type: "bar",
    data: [
      {
        label: "KPI values",
        backgroundColor: "#303F9F",
        borderColor: "#303F9F",
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
  },
  {
    type: "area",
    data: [
      {
        label: "KPI values",
        backgroundColor: "#303F9F",
        borderColor: "#303F9F",
        fill: true,
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
  },
  {
    type: "line",
    data: [
      {
        label: "KPI values",
        backgroundColor: "#303F9F",
        borderColor: "#303F9F",
        fill: false,
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
  },
  {
    type: "doughnut",
    data: [
      {
        label: "KPI values",
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
  },
];

// Grafici per KPI qualitativi
const qualitativeCharts = [
  {
    id: "doughnut",
    type: "doughnut",
    data: [
      {
        label: "Distribuzione Categorie",
        data: chartData.value,
        backgroundColor: generateColors(chartLabels.value.length),
      },
    ],
    labels: chartLabels.value,
  },
  {
    id: "bar-vertical",
    type: "bar",
    data: [
      {
        label: "Distribuzione Categorie",
        backgroundColor: generateColors(chartLabels.value.length),
        borderColor: generateColors(chartLabels.value.length),
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
    horizontal: false,
  },
  {
    id: "bar-horizontal",
    type: "bar",
    data: [
      {
        label: "Distribuzione Categorie",
        backgroundColor: generateColors(chartLabels.value.length),
        borderColor: generateColors(chartLabels.value.length),
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
    horizontal: true,
  },
  {
    id: "polarArea",
    type: "polarArea",
    data: [
      {
        label: "Distribuzione Categorie",
        data: chartData.value,
        backgroundColor: generateColors(chartLabels.value.length),
      },
    ],
    labels: chartLabels.value,
  },
  {
    id: "radar",
    type: "radar",
    data: [
      {
        label: "Distribuzione Categorie",
        backgroundColor: "rgba(48, 63, 159, 0.2)",
        borderColor: "#303F9F",
        data: chartData.value,
      },
    ],
    labels: chartLabels.value,
  },
];

// Seleziona i tipi di grafico in base al tipo di KPI
const chartTypes = computed(() => {
  return isQualitative.value ? qualitativeCharts : quantitativeCharts;
});

const selectedChart = ref("");

function selectChart(chart) {
  selectedChart.value = chart;
  emit("update-chart-type", chart);
}

/**
 * Creates an array of 12 integer values randomly generated around `centerValue`.
 */
function createArrayOfRandomValues(centerValue) {
  let array = [centerValue];
  let increment = Math.floor(centerValue / 2);
  let usedValues = new Set([centerValue]);

  while (usedValues.size < 12) {
    let randomIncrement = Math.floor(Math.random() * increment) + 1;
    let newValue =
      centerValue + (Math.random() < 0.5 ? -randomIncrement : randomIncrement);
    if (!usedValues.has(newValue)) {
      usedValues.add(newValue);
      array.push(newValue);
    }
  }
  // Shuffle the array to randomly position the values
  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Genera dati di frequenza per ogni categoria qualitativa.
 * Crea conteggi casuali per ogni valore consentito, dando più peso alla categoria target.
 */
function generateQualitativeData(allowedValues, targetValue) {
  if (!allowedValues || allowedValues.length === 0) {
    return [];
  }

  const targetIndex = allowedValues.indexOf(targetValue);
  const frequencies = [];

  allowedValues.forEach((value, index) => {
    // Genera frequenze tra 3 e 15
    let frequency = Math.floor(Math.random() * 13) + 3;
    
    // Se è la categoria target, aumenta la frequenza del 30-50%
    if (index === targetIndex && targetIndex !== -1) {
      frequency = Math.floor(frequency * (1.3 + Math.random() * 0.2));
    }
    
    frequencies.push(frequency);
  });

  return frequencies;
}

/**
 * Genera un array di colori distinti per le categorie qualitative.
 * La categoria target riceve un colore verde distintivo.
 */
function generateColors(count) {
  const colors = [
    "#4CAF50", // Verde per target
    "#2196F3", // Blu
    "#FFC107", // Giallo
    "#FF5722", // Rosso-arancio
    "#9C27B0", // Viola
    "#00BCD4", // Ciano
    "#FF9800", // Arancione
    "#607D8B", // Grigio-blu
  ];

  // Se servono più colori di quelli predefiniti, genera colori casuali
  if (count > colors.length) {
    for (let i = colors.length; i < count; i++) {
      const hue = (i * 360) / count;
      colors.push(`hsl(${hue}, 70%, 50%)`);
    }
  }

  return colors.slice(0, count);
}

onMounted(() => {
  const availableCharts = isQualitative.value
    ? qualitativeCharts
    : quantitativeCharts;

  if (
    props.sessionItem &&
    props.sessionItem.dashboard &&
    props.sessionItem.dashboard.chart &&
    props.sessionItem.dashboard.chart.type
  ) {
    const savedType = props.sessionItem.dashboard.chart.type;
    const savedHorizontal = props.sessionItem.dashboard.chart.horizontal || false;
    
    // Per i grafici qualitativi, cerca anche considerando l'orientamento horizontal
    if (isQualitative.value && savedType === "bar") {
      selectedChart.value = availableCharts.find(
        (chart) => chart.type === savedType && (chart.horizontal || false) === savedHorizontal
      );
    } else {
      selectedChart.value = availableCharts.find(
        (chart) => chart.type === savedType
      );
    }
  }
  
  if (!selectedChart.value) {
    selectedChart.value = availableCharts[0];
  }
});
</script>
